import { useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { getError, useToast } from '@aslanpay/portal-common-lib';

import useCancelCampaign from 'app/hooks/useCancelCampaign';

const MAX_REASON = 500;

interface CancelCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The cycle, to stop every pride under it, or one pride to stop that pride alone. */
  campaignId: string;
  title: string;
  /** What stops, in the words that fit what was named. */
  summary: string;
  successDescription: string;
}

/**
 * Stopping a campaign, or one pride of it.
 * <p>
 * Its nominations and any decision are kept and anything held against its pot is released, so this is not a
 * delete: it is the campaign ending early with its history intact.
 */
function CancelCampaignModal({
  isOpen,
  onClose,
  campaignId,
  title,
  summary,
  successDescription,
}: CancelCampaignModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const { mutate: cancel, isLoading } = useCancelCampaign({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Cancelled',
        description: successDescription,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
      setReason('');
      onClose();
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not cancel',
        description: getError(error).message || 'Nothing was changed. Please try again.',
      }),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="cancel-campaign-modal">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="16px">
            <Text textStyle="smallRegular" color="brand.black">
              {summary} Nominations already made and any decision already recorded are kept, and
              anything held against the funding pot is released.
            </Text>
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              This cannot be undone.
            </Text>
            <FormControl>
              <FormLabel>Why is it stopping?</FormLabel>
              <Textarea
                value={reason}
                maxLength={MAX_REASON}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Recorded against the campaign, so anyone reading it later sees why"
                data-testid="cancel-reason-input"
              />
              <FormHelperText>
                Optional. {MAX_REASON - reason.length} characters left.
              </FormHelperText>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={onClose} data-testid="keep-campaign">
            Keep it running
          </Button>
          <Button
            colorScheme="red"
            onClick={() => cancel({ campaignId, reason: reason.trim() || undefined })}
            isLoading={isLoading}
            data-testid="confirm-cancel-campaign"
          >
            Cancel it
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CancelCampaignModal;
